/**
 * Post-auth landing route resolver.
 *
 * A role entry is inspected in this order: platformRole (super_admin → /admin)
 * → preferred role from localStorage (if the user previously switched roles)
 * → first propertyRole (PM/supervisor → /manager, enterprise → /enterprise,
 * employee → /employee, org_admin → /org, resident → /resident).
 * Falls back to /resident when neither is present — matches the previous
 * login-form default.
 *
 * Shared by LoginForm (LoginSuccessResponse), MfaPage / MfaSetupPage (MeProfile
 * fetched via getMe). The shape is loose so both inputs fit without casts.
 */

const PREFERRED_ROLE_KEY = 'sg_preferred_role';

function roleToRoute(role: string | undefined): string {
  switch (role) {
    case 'property_manager':
    case 'security_supervisor':
      return '/manager';
    case 'guard':
      return '/guard';
    case 'enterprise_tenant_admin':
      return '/enterprise';
    case 'employee':
      return '/employee';
    case 'org_admin':
      return '/org';
    case 'resident':
    default:
      return '/resident';
  }
}

export function getRoleHomeRoute(profile: {
  platformRole?: string | null;
  propertyRoles?: ReadonlyArray<{ role: string }>;
}): string {
  if (profile.platformRole === 'super_admin') return '/admin';

  // Check if the user has a stored role preference and still holds that role.
  try {
    const preferred = localStorage.getItem(PREFERRED_ROLE_KEY);
    if (preferred && profile.propertyRoles?.some((r) => r.role === preferred)) {
      return roleToRoute(preferred);
    }
  } catch {
    // localStorage unavailable — fall through to default
  }

  const role = profile.propertyRoles?.[0]?.role;
  return roleToRoute(role);
}
